/**
 * Hierarchy Slice
 * 
 * Manages cycles and processes data with caching
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cycle, Process } from '../../services/hierarchyService';
import { resetReduxState } from '../actions';
import { RiskRanking } from '../../services/types';

export interface HierarchyState {
  // Cycles data for current annual report
  cycles: Cycle[];
  lastFetchedReportId: string | null;

  // Processes data mapped by cycleId
  processesByCycleId: Record<string, Process[]>;
  fetchedCycleIds: string[];
}

const initialState: HierarchyState = {
  cycles: [],
  lastFetchedReportId: null,
  processesByCycleId: {},
  fetchedCycleIds: [],
};

const hierarchySlice = createSlice({
  name: 'hierarchy',
  initialState,
  reducers: {
    // Set cycles data
    setCycles: (
      state,
      action: PayloadAction<{ cycles: Cycle[]; reportId: string }>
    ) => {
      state.cycles = action.payload.cycles;
      state.lastFetchedReportId = action.payload.reportId;
    },

    // Set processes for a specific cycle
    setProcessesForCycle: (
      state,
      action: PayloadAction<{ cycleId: string; processes: Process[] }>
    ) => {
      const { cycleId, processes } = action.payload;
      state.processesByCycleId[cycleId] = processes;
      if (!state.fetchedCycleIds.includes(cycleId)) {
        state.fetchedCycleIds.push(cycleId);
      }
    },

    // Clear all hierarchy data
    clearHierarchyData: (state) => {
      state.cycles = [];
      state.lastFetchedReportId = null;
      state.processesByCycleId = {};
      state.fetchedCycleIds = [];
    },

    // Clear processes for a specific cycle (useful for refresh)
    clearProcessesForCycle: (state, action: PayloadAction<string>) => {
      const cycleId = action.payload;
      delete state.processesByCycleId[cycleId];
      state.fetchedCycleIds = state.fetchedCycleIds.filter(id => id !== cycleId);
    },

    // Clear all processes cache (useful after cycle deletion when process codes change)
    clearAllProcesses: (state) => {
      state.processesByCycleId = {};
      state.fetchedCycleIds = [];
    },

    // Update process risk ranking (for sync with RiskAssessment)
    updateProcessRiskRanking: (
      state,
      action: PayloadAction<{ cycleCode: string; processCode: string; riskRanking: RiskRanking }>
    ) => {
      const { cycleCode, processCode, riskRanking } = action.payload;
      
      // Find the cycle by cycleCode
      const cycle = state.cycles.find(c => c.cycleCode === cycleCode);
      if (!cycle) return;
      
      // Find and update the process in processesByCycleId
      const processes = state.processesByCycleId[cycle.id];
      if (!processes) return;
      
      const process = processes.find(p => p.processCode === processCode);
      if (process) {
        process.riskRanking = riskRanking;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setCycles,
  setProcessesForCycle,
  clearHierarchyData,
  clearProcessesForCycle,
  clearAllProcesses,
  updateProcessRiskRanking,
} = hierarchySlice.actions;

export default hierarchySlice.reducer;

